import { DateTime } from "luxon";
import { useState, useEffect } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/src/style.css";
import Switch from "react-switch";
import { TimeFinderOption } from "../../../../src/api/trpc/time-finder";
import { TimePicker } from "../../dashboard/forms/time_picker";
import { WithDropdownImproved } from "../../dashboard/forms/improved_components/base_components/with_dropdown_improved";
import { createNewTimeFinderOption } from "../utils";

interface DateGridViewProps {
	selectedDates: TimeFinderOption[];
	onUpdate: (date_options: TimeFinderOption[]) => void;
}

interface DateListViewProps {
	datesWithTimes: TimeFinderOption[];
	onUpdate: (date_options: TimeFinderOption[]) => void;
}

const formatDate = (date: Date): string => {
	return DateTime.fromJSDate(date).toFormat("LLL d, yyyy");
};

const areDatesEqual = (date1: TimeFinderOption, date2: TimeFinderOption) => {
	return (
		date1.date.day === date2.date.day &&
		date1.date.month === date2.date.month &&
		date1.date.year === date2.date.year
	);
};

// Grid view component (original view)
const DateGridView = ({ selectedDates, onUpdate }: DateGridViewProps) => {
	return (
		<div className="flex flex-wrap gap-2">
			{selectedDates.map((date) => {
				const date_obj = DateTime.fromObject(date.date);

				return (
					<div
						key={date_obj.toISO()}
						className="group relative flex h-16 w-16 cursor-pointer flex-col overflow-hidden rounded-sm border border-base-dark shadow-sm transition-all hover:border-sesh-primary"
						onClick={() =>
							onUpdate(
								selectedDates.filter(
									(val) => !areDatesEqual(val, date)
								)
							)
						}
					>
						<div className="w-full bg-base-light py-0.5 text-center">
							<span className="text-xs font-medium text-content-primary">
								{date_obj.toFormat("LLL")}
							</span>
						</div>
						<div className="flex flex-1 items-center justify-center bg-sesh-dark">
							<span className="text-lg font-bold text-black">
								{date_obj.toFormat("d")}
							</span>
						</div>
					</div>
				);
			})}
		</div>
	);
};

function getNextAvailableHour(
	timeslots: TimeFinderOption["timeslots"]
): TimeFinderOption["timeslots"][number] {
	const lastTimeslot = timeslots[timeslots.length - 1];

	const date = DateTime.fromMillis(lastTimeslot.start_time)
		.set({ minute: 0, second: 0 })
		.plus({ hour: 1 });

	return {
		start_time: date.toMillis(),
		end_time: date.plus({ hour: 1 }).toMillis(),
	};
}

function applyTimesToAllDates(
	datesWithTimes: TimeFinderOption[],
	dateWithTimes: TimeFinderOption
): TimeFinderOption[] {
	if (dateWithTimes.timeslots.length === 0) return datesWithTimes;

	const currentTimeslots = dateWithTimes.timeslots;
	return datesWithTimes.map((date) => {
		const luxon_date = DateTime.fromObject(date.date);

		return {
			...date,
			timeslots: currentTimeslots.map((timeslot) => {
				const start_hour = DateTime.fromMillis(
					timeslot.start_time
				).hour;
				const start_minute = DateTime.fromMillis(
					timeslot.start_time
				).minute;

				const end_hour = DateTime.fromMillis(timeslot.end_time).hour;
				const end_minute = DateTime.fromMillis(
					timeslot.end_time
				).minute;

				const new_start_time = luxon_date.set({
					hour: start_hour,
					minute: start_minute,
				});

				const new_end_time = luxon_date.set({
					hour: end_hour,
					minute: end_minute,
				});

				return {
					start_time: new_start_time.toMillis(),
					end_time: new_end_time.toMillis(),
				};
			}),
		};
	});
}

// Vertical list view component with time options
const DateListView = ({ datesWithTimes, onUpdate }: DateListViewProps) => {
	return (
		<div className="flex max-h-[400px] flex-col gap-4 overflow-y-auto pr-2">
			{datesWithTimes.map((dateWithTimes, dateIndex) => {
				const date = DateTime.fromObject(dateWithTimes.date);

				return (
					<div
						key={
							dateWithTimes.date.year +
							"/" +
							dateWithTimes.date.month +
							"/" +
							dateWithTimes.date.day
						}
						className="rounded-md border border-base-dark bg-base-dark"
					>
						{/* Date header */}
						<div className="flex items-center justify-between border-b border-base-light px-4 py-3">
							<div className="flex items-center gap-2">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									className="h-5 w-5 text-white"
									viewBox="0 0 20 20"
									fill="currentColor"
								>
									<path
										fillRule="evenodd"
										d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
										clipRule="evenodd"
									/>
								</svg>
								<h4 className="font-medium text-white">
									{date.toFormat("EEEE, MMMM d, yyyy")}
								</h4>
							</div>
							<button
								className="text-sm text-white hover:text-red-300"
								onClick={() =>
									onUpdate(
										datesWithTimes.filter(
											(val) =>
												val.date !== dateWithTimes.date
										)
									)
								}
							>
								Remove
							</button>
						</div>

						{/* Time slots section */}
						<div className="bg-base-light p-3">
							<div className="time-options space-y-2">
								{dateWithTimes.timeslots.map((timeOption) => {
									const base_id = JSON.stringify(
										dateWithTimes.date
									);

									const local_start_time =
										DateTime.fromMillis(
											timeOption.start_time
										).toLocal();
									const local_end_time = DateTime.fromMillis(
										timeOption.end_time
									).toLocal();

									const start_time_hour =
										local_start_time.hour;
									const start_time_minute =
										local_start_time.minute;

									const start_time_ms =
										start_time_hour * 60 * 60 * 1000 +
										start_time_minute * 60 * 1000;

									const end_time_hour = local_end_time.hour;
									const end_time_minute =
										local_end_time.minute;

									const end_time_ms =
										end_time_hour * 60 * 60 * 1000 +
										end_time_minute * 60 * 1000;

									const duration =
										end_time_ms - start_time_ms;

									const start_id =
										base_id +
										":" +
										timeOption.start_time +
										"-start";

									return (
										<div
											key={start_id}
											className="sesh-date-picker time-option-row relative flex items-center gap-3"
										>
											<div className="flex-1">
												<TimePicker
													initial_value_ms={
														start_time_ms
													}
													updateFunc={(ms) => {
														const minutes =
															ms / 1000 / 60;
														const hours =
															Math.floor(
																minutes / 60
															);
														const minutes_remainder =
															minutes % 60;

														const new_start_time =
															date.set({
																hour: hours,
																minute: minutes_remainder,
															});

														timeOption.start_time =
															new_start_time.toMillis();

														timeOption.end_time =
															new_start_time
																.plus({
																	millisecond:
																		duration,
																})
																.toMillis();

														onUpdate(
															datesWithTimes
														);
													}}
													disabled={false}
													portal={true}
												/>
											</div>
											<div className="hidden px-1 py-1 font-medium text-sesh-dark md:block">
												to
											</div>
											<div className="flex-1">
												<TimePicker
													initial_value_ms={
														end_time_ms
													}
													updateFunc={(ms) => {
														const minutes =
															ms / 1000 / 60;
														const hours =
															Math.floor(
																minutes / 60
															);
														const minutes_remainder =
															minutes % 60;

														timeOption.end_time =
															date
																.set({
																	hour: hours,
																	minute: minutes_remainder,
																})
																.toMillis();

														onUpdate(
															datesWithTimes
														);
													}}
													disabled={false}
													portal={true}
												/>
											</div>
											<button
												className="z-10 flex h-8 w-8 items-center justify-center rounded-full bg-base-dark text-xl text-white hover:bg-red-500"
												onClick={() => {
													dateWithTimes.timeslots =
														dateWithTimes.timeslots.filter(
															(val) =>
																val !==
																timeOption
														);
													onUpdate(datesWithTimes);
												}}
											>
												×
											</button>
										</div>
									);
								})}
							</div>

							<div className="mt-3 flex items-center gap-2">
								<div className="flex w-fit flex-grow">
									<button
										className="flex items-center whitespace-nowrap rounded-md border-2 border-sesh-dark p-2 px-3 text-sm font-medium"
										onClick={() => {
											dateWithTimes.timeslots.push(
												getNextAvailableHour(
													dateWithTimes.timeslots
												)
											);
											onUpdate(datesWithTimes);
										}}
									>
										+ Add Time
									</button>
								</div>
								<div className="w-fit">
									<WithDropdownImproved
										alignment="left"
										items={[
											{
												type: "button",
												label: "Apply times to all dates",
												close_on_click: true,
												icon: (
													<svg
														xmlns="http://www.w3.org/2000/svg"
														className="h-5 w-5"
														viewBox="0 0 20 20"
														fill="currentColor"
													>
														<path d="M7 9a2 2 0 012-2h6a2 2 0 012 2v6a2 2 0 01-2 2H9a2 2 0 01-2-2V9z" />
														<path d="M5 3a2 2 0 00-2 2v6a2 2 0 002 2V5h8a2 2 0 00-2-2H5z" />
													</svg>
												),
												props: {
													onClick: () => {
														onUpdate(
															applyTimesToAllDates(
																datesWithTimes,
																dateWithTimes
															)
														);
													},
												},
											},
											{
												type: "button",
												label: "Remove",
												close_on_click: true,
												is_warning: true,
												icon: (
													<svg
														xmlns="http://www.w3.org/2000/svg"
														className="h-5 w-5"
														viewBox="0 0 20 20"
														fill="currentColor"
													>
														<path
															fillRule="evenodd"
															d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
															clipRule="evenodd"
														/>
													</svg>
												),
												props: {
													onClick: () => {
														onUpdate(
															datesWithTimes.filter(
																(val) =>
																	!areDatesEqual(
																		val,
																		dateWithTimes
																	)
															)
														);
													},
												},
											},
										]}
									>
										<div className="flex h-8 w-8 cursor-pointer items-center justify-center hover:bg-base-light">
											<svg
												xmlns="http://www.w3.org/2000/svg"
												className="h-5 w-5 text-sesh-dark transition-colors hover:text-sesh-primary"
												viewBox="0 0 20 20"
												fill="currentColor"
											>
												<path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z" />
											</svg>
										</div>
									</WithDropdownImproved>
								</div>
							</div>
						</div>
					</div>
				);
			})}
		</div>
	);
};

export function CalendarPicker({
	selectedDates,
	onSpecifyTimesChange,
	onUpdate,
	areTimesSpecified,
}: {
	selectedDates: TimeFinderOption[]; // These are all startTime DateTimes
	areTimesSpecified: boolean;
	onUpdate: (date_options: TimeFinderOption[]) => void;
	onSpecifyTimesChange?: (specifyTimes: boolean) => void;
}) {
	const today = DateTime.now().startOf("day");

	// State for controlled selected prop
	const [selectedDays, setSelectedDays] = useState<Date[]>(
		Array.from(
			new Set(
				selectedDates.map((d) => {
					const date_obj = DateTime.fromObject(d.date);
					return date_obj.startOf("day").toJSDate();
				})
			)
		)
	);

	// Update selectedDays when selectedDates prop changes
	useEffect(() => {
		setSelectedDays(
			Array.from(
				new Set(
					selectedDates.map((d) => {
						const date_obj = DateTime.fromObject(d.date);
						return date_obj.startOf("day").toJSDate();
					})
				)
			)
		);
	}, [selectedDates]);

	const renderSelectedDates = () => {
		if (selectedDates.length === 0) {
			return (
				<div className="flex h-20 items-center justify-center rounded-md border border-dashed bg-base-dark">
					<p className="text-sm text-gray-500">No dates selected</p>
				</div>
			);
		}

		return areTimesSpecified ? (
			<DateListView datesWithTimes={selectedDates} onUpdate={onUpdate} />
		) : (
			<DateGridView selectedDates={selectedDates} onUpdate={onUpdate} />
		);
	};

	return (
		<div className="calendar-picker flex w-full flex-col items-start justify-items-center gap-6">
			<style>{`
				.time-finder-calendar .rdp-chevron {
					fill: #00f4ff !important;
				}
				.time-finder-calendar .rdp-day_button:focus {
					outline-color: #00f4ff !important;
				}
				.time-finder-calendar .rdp-today:not(.rdp-outside):not(.rdp-selected) {
					color: #00f4ff !important;
				}
				.time-finder-calendar .rdp-today.rdp-selected {
					color: black !important;
				}
				.time-finder-calendar .rdp-today .rdp-day_button {
					border: none !important;
				}
			`}</style>
			<div className="flex w-full flex-col items-start gap-6 lg:flex-row">
				<div className="mx-auto flex w-full justify-center lg:w-1/2">
					<div 
						className="time-finder-calendar"
						style={{
							// Override react-day-picker CSS variables
							// @ts-ignore
							"--rdp-accent-color": "#00f4ff",
							"--rdp-accent-background-color": "rgba(0, 244, 255, 0.2)",
							"--rdp-selected-border": "none",
							"--rdp-today-color": "#00f4ff",
							"--rdp-chevron-color": "#00f4ff",
						}}
					>
						<DayPicker
						mode="multiple"
						selected={selectedDays}
						onDayClick={(day) => {
							if (day < today.toJSDate()) return;

							// Check if day is already selected
							const isSelected = selectedDates.some((d) => {
								const date_obj = DateTime.fromObject(d.date);
								const dayStart = date_obj.startOf("day").toJSDate();
								return dayStart.getTime() === day.getTime();
							});

							if (isSelected) {
								// Remove the date
								const newDates = selectedDates.filter((d) => {
									const date_obj = DateTime.fromObject(d.date);
									const dayStart = date_obj.startOf("day").toJSDate();
									return dayStart.getTime() !== day.getTime();
								});
								onUpdate(newDates);
							} else {
								// Add the date
								onUpdate([
									...selectedDates,
									createNewTimeFinderOption(day),
								]);
							}
						}}
						disabled={{ before: today.toJSDate() }}
						modifiersStyles={{
							selected: {
								backgroundColor: "#00f4ff",
								color: "black",
								borderRadius: "4px",
							},
							today: {
								fontWeight: "bold",
								borderRadius: "4px",
							},
						}}
						styles={{
							day_button: {
								borderRadius: "4px",
								border: "none",
							},
							selected: {
								border: "none",
							},
							chevron: {
								fill: "#00f4ff",
							},
						}}
					/>
					</div>
				</div>
				<div className="selected-dates w-full lg:w-1/2">
					<div className="mb-4 mt-4 flex items-center justify-between">
						<h3 className="text-lg font-medium">Selected Dates</h3>
						<div className="toggle-container flex items-center">
							<span className="mr-2 text-sm">Specify Times</span>
							<Switch
								checked={areTimesSpecified}
								onChange={onSpecifyTimesChange}
								onColor="#00c3cc"
								onHandleColor="#00f4ff"
								checkedIcon={
									<span className="flex h-full flex-col items-center justify-center text-black opacity-50">
										<i className="fas fa-check"></i>
									</span>
								}
								uncheckedIcon={
									<span className="flex h-full flex-col items-center justify-center text-black opacity-50">
										<i className="fas fa-times"></i>
									</span>
								}
							/>
						</div>
					</div>

					{renderSelectedDates()}
				</div>
			</div>
		</div>
	);
}
